import { type Competenza } from '@framework/api-client/api/inquiries-compile/schemas/competenza.ts';
import { type PersonalizzazioniRiepilogoDTO } from '@framework/api-client/api/inquiries-compile/schemas/personalizzazioniRiepilogoDTO.ts';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { type TipoDomandaDTO } from '@framework/api-client/api/inquiries-compile/schemas/tipoDomandaDTO.ts';
import { t, type useTranslation } from '@framework/i18n/intl/i18next.ts';
import { VerticalSpace } from '@framework/ui/components/space/VerticalSpace.tsx';
import { SectionTitle } from '@framework/ui/components/summary/SectionTitle.tsx';
import { SummaryDetailCard } from '@framework/ui/components/summary/SummaryDetailCard.tsx';
import { Flex } from 'antd';
import { useMemo } from 'react';
import {
  TbBuilding,
  // Comune/Sportello
  TbClipboardTypography, // Tipo Domanda
  TbFileDescription,
  TbMapPinCheck, // Oggetto della Richiesta
  TbUserCheck, // Responsabile del procedimento
  // TbUser, // Titolare richiesta (persona fisica)
  // TbBuildingStore, // Titolare richiesta (persona giuridica)
  // TbUserShield, // Legale rappresentante
  // TbGavel, // Procuratore
} from 'react-icons/tb';
import { useSummaryAdditionalData } from '../SummaryAdditionalDataContext.tsx';
import { SummaryGeneralDataItem } from './detail/SummaryGeneralDataItem.tsx';

export interface IGeneralDataItem {
  label: string;
  children: string;
  icon?: React.ReactNode;
  id: string;
}

interface IGetGeneralDataItemsOptions {
  submission?: RichiestaDTO;
  customizations?: PersonalizzazioniRiepilogoDTO | null;
  officeName?: string;
  responsibleName?: string;
  competenceName?: string;
  t: ReturnType<typeof useTranslation>['t'];
}

function getGeneralDataItems(
  options: IGetGeneralDataItemsOptions,
): NonNullable<IGeneralDataItem>[] {
  const {
    submission,
    customizations,
    officeName,
    responsibleName,
    competenceName,
    t,
  } = options;
  const result: NonNullable<IGeneralDataItem>[] = [];

  if (!customizations?.sportello?.nascondi && officeName) {
    result.push({
      id: 'sportello',
      label:
        customizations?.sportello?.etichetta ??
        t('submission-summary.ufficio-competente', 'Sportello'),
      children: officeName,
      icon: <TbBuilding />,
    });
  }

  // Non esiste una personalizzazione per questa voce in
  // PersonalizzazioniRiepilogoDTO: non è né nascondibile né rietichettabile.
  if (responsibleName) {
    result.push({
      id: 'responsabile',
      label: t(
        'submission-summary.responsabile-procedimento',
        'Responsabile del procedimento',
      ),
      children: responsibleName,
      icon: <TbUserCheck />,
    });
  }

  if (!customizations?.comunePrevalente?.nascondi && competenceName) {
    result.push({
      id: 'comunePrevalente',
      label:
        customizations?.comunePrevalente?.etichetta ??
        t('submission-summary.comune-prevalente', 'Comune prevalente'),
      children: competenceName,
      icon: <TbMapPinCheck />,
    });
  }

  if (!customizations?.tipoDomanda?.nascondi && submission?.titoloTipoDomanda) {
    result.push({
      id: 'tipoDomanda',
      label:
        customizations?.tipoDomanda?.etichetta ??
        t('submission-summary.tipo-domanda', 'Tipo domanda'),
      children: submission?.titoloTipoDomanda ?? '-',
      icon: <TbClipboardTypography />,
    });
  }

  if (!customizations?.oggettoRichiesta?.nascondi && submission?.oggetto) {
    result.push({
      id: 'oggettoRichiesta',
      label:
        customizations?.oggettoRichiesta?.etichetta ??
        t('submission-summary.oggetto-richiesta', 'Oggetto richiesta'),
      children: submission?.oggetto ?? '-',
      icon: <TbFileDescription />,
    });
  }

  return result;
}

export interface ISummaryGeneralDataSectionProps {
  submission?: RichiestaDTO;
  inquiryType?: TipoDomandaDTO;
  customizations?: PersonalizzazioniRiepilogoDTO | null;
}

function findCompetence(
  competences: Competenza[] | null | undefined,
  competenceId: string | null | undefined,
): Competenza | undefined {
  if (!(competences && competenceId)) return undefined;
  return competences.find(c => c.idCompetenza === competenceId);
}

function findOfficeName(
  competence: Competenza | undefined,
  officeId: string | null | undefined,
): string | undefined {
  if (!competence?.gruppiCompetenza) return undefined;
  if (!officeId) return undefined;
  return (
    competence.gruppiCompetenza.find(g => g.idGruppoCompetenza === officeId)
      ?.denominazione ?? undefined
  );
}

/**
 * Sezione del riepilogo che mostra i dati generali della richiesta
 */
export function SummaryGeneralDataSection(
  props: Readonly<ISummaryGeneralDataSectionProps>,
) {
  const { submission, inquiryType, customizations } = props;
  const additionalData = useSummaryAdditionalData();

  const matchedCompetence = useMemo(
    () =>
      findCompetence(
        inquiryType?.competenze,
        submission?.idCompetenzaUfficioResp,
      ),
    [inquiryType?.competenze, submission?.idCompetenzaUfficioResp],
  );

  /**
   * `ufficioResponsabileProcedimento` arriva già risolto nella GET della
   * richiesta e ha la precedenza: la derivazione client-side risale a
   * `matchedCompetence.gruppiCompetenza`, che dipende da
   * `inquiryType.competenze` — non sempre popolato — e da
   * `idUfficioResponsabile`, spesso nullo.
   */
  const officeName = useMemo(
    () =>
      submission?.ufficioResponsabileProcedimento ??
      findOfficeName(matchedCompetence, submission?.idUfficioResponsabile),
    [
      submission?.ufficioResponsabileProcedimento,
      matchedCompetence,
      submission?.idUfficioResponsabile,
    ],
  );

  /**
   * `nomeCompetenza` è risolto dal BE e ha la precedenza: la derivazione
   * client-side dipende da `inquiryType.competenze`, che non è sempre
   * popolato, e in quel caso la voce sparirebbe del tutto.
   */
  const competenceName =
    additionalData?.nomeCompetenza ?? matchedCompetence?.denominazione;

  const items = useMemo(
    () =>
      getGeneralDataItems({
        submission,
        customizations,
        officeName,
        responsibleName: additionalData?.nomeResponsabile,
        competenceName,
        t,
      }),
    [
      submission,
      customizations,
      officeName,
      additionalData?.nomeResponsabile,
      competenceName,
      t,
    ],
  );

  if (items.length === 0) return null;

  return (
    <div>
      <SectionTitle
        icon={<TbFileDescription />}
        title={t('submission-summary.dati-generali', 'Dati Generali')}
      />
      <VerticalSpace />
      <SummaryDetailCard>
        <Flex vertical gap={24}>
          {items.map(item => (
            <SummaryGeneralDataItem key={item.id} item={item} />
          ))}
        </Flex>
      </SummaryDetailCard>
    </div>
  );
}
